import React from "react";
import useInstructionsVisibility from "../hooks/useInstructionsVisibility";

/**
 * Instructions — calm, editorial callout panel that lives on every screen
 * of the flow to give the patient context on what to do and why.
 *
 * Respects a global "show instructions" preference stored in localStorage.
 * Each panel exposes a "Hide future instructions" button that hides ALL
 * instruction panels app-wide (re-enable on Welcome).
 *
 * Props:
 *   title    — short eyebrow label (e.g. "How to use this screen")
 *   items    — array of strings OR { strong, text } objects for emphasis
 *   tone     — "blue" (default) | "red" | "neutral"
 */
export default function Instructions({ title = "How to use this screen", items = [], tone = "blue" }) {
  const { visible, hide } = useInstructionsVisibility();
  if (!visible) return null;

  const accent =
    tone === "red"
      ? "var(--accent-red)"
      : tone === "neutral"
      ? "var(--ink-mute)"
      : "var(--accent-blue)";
  const bg =
    tone === "red"
      ? "var(--accent-red-soft)"
      : tone === "neutral"
      ? "var(--bg-tint)"
      : "var(--accent-blue-soft)";

  return (
    <div
      className="px-6 py-5 mb-8"
      style={{
        background: bg,
        border: `1px solid ${accent}24`,
        borderRadius: "2px",
      }}
      data-testid="instructions-card"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div
          className="iw-mono uppercase"
          style={{ color: accent, fontSize: "11px", letterSpacing: "0.24em", fontWeight: 700 }}
        >
          {title}
        </div>
        <button
          type="button"
          onClick={hide}
          className="iw-mono uppercase text-mute hover:text-ink transition-colors flex-shrink-0"
          style={{ fontSize: "10px", letterSpacing: "0.18em", fontWeight: 600 }}
          data-testid="instructions-hide-btn"
          title="Hide instructions on every screen — you can re-enable on the Welcome screen"
        >
          Hide future instructions →
        </button>
      </div>
      <ol className="space-y-2 list-none m-0 p-0">
        {items.map((item, i) => {
          const node = typeof item === "string" ? { text: item } : item;
          const stableKey = `${i}-${(node.strong || node.text || "").slice(0, 32)}`;
          return (
            <li key={stableKey} className="flex items-start gap-3 leading-relaxed">
              <span
                className="iw-mono flex-shrink-0 mt-1.5 font-bold w-5"
                style={{ color: accent, fontSize: "11px" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[15px] flex-1" style={{ color: "var(--iw-primary)" }}>
                {node.strong && (
                  <span className="font-bold">{node.strong} — </span>
                )}
                {node.text}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
